import { lazy, StrictMode, Suspense, useEffect, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { DiagnosticsPage } from './diagnostics/diagnostics_page.tsx';
import { CloudPage, useCloudPage } from './screens/cloud_page.tsx';
import { DashboardApp } from './screens/dashboard_app.tsx';
import { HomePage } from './screens/home_page.tsx';
import { JoinRequests } from './settings/join_requests.tsx';
import { SettingsApp } from './settings/settings_app.tsx';
import { settingsRoute } from './settings/settings_navigation.ts';
import './index.css';
// Registers window.__casaSpike for the report scripts, whichever page is shown.
import './diagnostics/spike.ts';
// Registers window.__casaConformance for scripts/conformance-report.mjs.
import './bridge/conformance_hook.ts';
import { bridge } from './bridge/native_bridge.ts';
import { applyAppSettings, readAppSettings } from './data/app_settings.ts';
import { ScreenSleep } from './device/screen_sleep_view.tsx';
import { startDiagnostics } from './diagnostics/diagnostics_setup.ts';
import { ChromeProvider } from './ui/chakra.tsx';
import { isBrowserPage, routeState, useRoute } from './ui/route.ts';
import { hideSplash } from './ui/splash.ts';

// The Tilez Hub's admin page, with CodeMirror, is loaded only when it is opened.
const AdminPage = lazy(async () => ({ default: (await import('./admin/admin_page.tsx')).AdminPage }));
// tilez-cloud's account pages (cloud-accounts.md), likewise.
const AccountPage = lazy(async () => ({ default: (await import('./account/account_page.tsx')).AccountPage }));

// The console into the log, and the remote log sink if the user turned it on. Either may find no
// bridge to read its settings from (tilez-cloud's pages have none): then there is nothing to apply.
startDiagnostics(bridge).catch((error: unknown) => console.warn(`diagnostics: the log sink's settings were not read: ${error instanceof Error ? error.message : String(error)}`));

// This device's settings (keep awake) apply from the start.
if (bridge) {
    const shell = bridge;
    readAppSettings(shell)
        .then((settings) => applyAppSettings(shell, settings))
        .then(
            (problem) => problem && console.warn(`settings: the screen is not kept on: ${problem}`),
            (error: unknown) => console.warn(`settings: not read: ${error instanceof Error ? error.message : String(error)}`),
        );
}

/**
 * The page by its route (ui/route.ts): in a browser a path, in a shell after the #. The dashboard at
 * /dash, a server's dashboard in this tab only at /dash/<name>; the settings (/settings,
 * /settings/<part>) lie over the dashboard, so it keeps running; the engine and network tests at
 * /diagnostics, the Tilez Hub's admin page at /admin (and /admin/<dashboard>), tilez-cloud's account
 * pages at /account. In a browser, / lists the server's dashboards.
 */
function App() : ReactNode {
    const route = useRoute();
    const { isCloud, hasAccounts } = useCloudPage(bridge);
    const isAdmin = route === '/admin' || route.startsWith('/admin/');
    const isAccount = (route === '/account' || route.startsWith('/account/')) && isBrowserPage();
    const isHome = route === '/' && isBrowserPage();

    const isOwnPage = isAdmin || isAccount || route === '/diagnostics' || isHome || isCloud === true;
    useEffect(() => {
        if (isOwnPage) {
            hideSplash();
        }
    }, [isOwnPage]);

    if (isAdmin) {
        return (
            <Suspense fallback={null}>
                <AdminPage route={route} />
            </Suspense>
        );
    }

    if (isAccount) {
        return (
            <Suspense fallback={null}>
                <AccountPage route={route} />
            </Suspense>
        );
    }

    if (route === '/diagnostics') {
        return <DiagnosticsPage />;
    }

    // A browser on tilez-cloud gets no dashboard (secrets QSE-8); nothing shows while the server is asked.
    if (isCloud !== false) {
        return isCloud ? <CloudPage hasAccounts={hasAccounts} /> : null;
    }

    if (isHome && bridge) {
        return <HomePage shell={bridge} />;
    }

    const settings = settingsRoute(route);

    // Opened from the start page, the settings lie over it, and close back to it.
    if (settings !== null && routeState().overHome === true && isBrowserPage() && bridge) {
        return (
            <>
                <HomePage shell={bridge} />
                <SettingsApp route={settings} />
            </>
        );
    }

    // The dashboard keeps its place in the tree whether the settings are open or not, so that it is not
    // started again when they close. The screen's sleep lies over both.
    return (
        <>
            <DashboardApp />
            {settings === null ? null : <SettingsApp route={settings} />}
            {bridge ? <ScreenSleep bridge={bridge} /> : null}
            {/* Another device that wants to join this one's home on Tilez Cloud (cloud-joining JN-4). */}
            {bridge ? <JoinRequests shell={bridge} /> : null}
        </>
    );
}

createRoot(document.getElementById('root')!).render(
    <StrictMode>
        <ChromeProvider>
            <App />
        </ChromeProvider>
    </StrictMode>,
);
